import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { EMPTY_STRING } from '../../constants/common'
import { QA_SECTION_KEY } from '../../constants/qa'
import { useScreen } from '../../hooks'
import { getQaSelector } from '../../utils/common'
import Button, { BUTTON_HEIGHT_24 } from '../Button'
import { FilterDivider } from '../FilterDivider'
import { ICON_HEIGHT_24 } from '../Icon'
import { NAV_ARROWS_MIN_VALUE, NavArrows } from '../NavArrow'
import { ButtonSelect } from '../Select'
import { getIsHiddenTableControlBarText } from '../Table'
import Text from '../Text'

import {
  QA_NAME_PAGINATION,
  QA_NAME_PAGINATION_DATA_SECTION,
  QA_NAME_PAGINATION_NEXT,
  QA_NAME_PAGINATION_PREV,
} from './Pagination.constants'
import { getOptions } from './Pagination.utils'

import * as styles from './Pagination.module.css'

function Pagination({
  containerRef,
  disabled,
  getIsHiddenTableText = getIsHiddenTableControlBarText,
  responsive,
  value,
  onChange,
  onChangeSize,
}) {
  const { t } = useTranslation()

  const { width: screenWidth } = useScreen({ containerRef })

  const isMobileScreenSize = getIsHiddenTableText({
    responsive,
    screenWidth,
  })

  const textClassName = classNames(styles.text, {
    [styles.disabled]: disabled,
  })

  function handleSetSize(option) {
    if (option.value !== value.pageSize) {
      onChangeSize(option.value)
    }
  }

  function handleClickNavArrow(nextValue) {
    if (!disabled) {
      onChange({ ...value, page: nextValue })
    }
  }

  if (!value.count) {
    return null
  }

  const firstValue = value.pageSize * value.page - value.pageSize + 1
  const lastValue = Math.min(value.pageSize * value.page, value.count)

  const qaProps = getQaSelector(
    { value: QA_NAME_PAGINATION_DATA_SECTION },
    QA_SECTION_KEY
  )

  return (
    <div {...qaProps} className={styles.pagination}>
      <Text isSecondary className={textClassName}>
        {isMobileScreenSize
          ? EMPTY_STRING
          : `${t('Table.ItemsPerPageLabelKey')}:`}
      </Text>
      <ButtonSelect
        className={styles.pageSize}
        controlProps={{
          borderless: true,
          className: styles.paginationSelect,
          contentProps: {
            className: styles.paginationSelectContent,
          },
          indicatorsProps: {
            ButtonClear: null,
            buttonArrowProps: {
              height: BUTTON_HEIGHT_24,
            },
            className: styles.paginationSelectArrow,
          },
          readonly: true,
        }}
        disabled={disabled}
        listProps={{
          headerProps: {
            Search: null,
          },
          itemProps: {
            contentProps: {
              infoProps: {
                contentTextProps: {
                  textProps: {
                    isSecondary: true,
                  },
                },
              },
            },
          },
        }}
        options={getOptions()}
        qaName={QA_NAME_PAGINATION}
        value={{ label: value.pageSize, value: value.pageSize }}
        onSelectItem={handleSetSize}
      />
      <FilterDivider />
      <Text isSecondary className={textClassName}>
        {isMobileScreenSize
          ? EMPTY_STRING
          : `${firstValue} - ${lastValue} ${t('Table.PageCountJointKey')} ${
              value.count
            }`}
      </Text>
      <NavArrows
        className={styles.arrows}
        maxValue={value.pageCount}
        minValue={NAV_ARROWS_MIN_VALUE}
        nextActionProps={{
          InnerComponent: Button.Text,
          isGrey: true,
          qaName: QA_NAME_PAGINATION_NEXT,
          size: ICON_HEIGHT_24,
          tooltip: t('Table.NextBtnTooltipKey'),
        }}
        prevActionProps={{
          InnerComponent: Button.Text,
          isGrey: true,
          qaName: QA_NAME_PAGINATION_PREV,
          size: ICON_HEIGHT_24,
          tooltip: t('Table.PreviosBtnTooltipKey'),
        }}
        size={ICON_HEIGHT_24}
        value={value.page}
        onChange={handleClickNavArrow}
      />
    </div>
  )
}

export default Pagination
